import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { OrderExpeditionPackageType } from 'src/types/orderExpeditionPackage';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import { EDIT_KEYS, EditKeysType } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import RenderComponents from './RenderComponents';
import {
    clearOrderExpeditionPackageDetail,
    deleteOrderExpeditionPackageDetailFetch,
    getOrderExpeditionPackageDetailFetch,
    updateOrderExpeditionPackageDetailFetch
} from 'src/resources/orderExpeditionPackageDetail/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType, OnChangeType } from '../../types/global';

const initData = { PRODUCTS: [] };

const OrderExpeditionPackageDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idOrderExpeditionPackage } = useParams();

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.orderExpeditionPackageDetail.data);
    const loading = useSelector((state: RootStoreType) => state.orderExpeditionPackageDetail.loading);
    const deleted = useSelector((state: RootStoreType) => state.orderExpeditionPackageDetail.deleted);

    const locationHistory = useSelector((state: RootStoreType) => state.location.history);

    const [editData, setEditData] = useState<OrderExpeditionPackageType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionUpdate = useCheckPermission('ORDER.EXPEDITION_PACKAGE.UPDATE');
    const permissionDelete = useCheckPermission('ORDER.EXPEDITION_PACKAGE.DELETE');

    const disableEdit = !permissionUpdate;

    const backUrl = '/orders/expedition_packages/';

    useEffect(() => {
        if (idOrderExpeditionPackage) {
            dispatch(
                setLocationParams({
                    name: translate('orderExpeditionPackageDetail.title', { idOrderExpeditionPackage }),
                    backUrl
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('orderExpeditionPackageDetail.titleNew'), backUrl }));
        }
    }, [idOrderExpeditionPackage, backUrl, locationHistory]);

    useEffect(() => {
        if (idOrderExpeditionPackage) {
            dispatch(getOrderExpeditionPackageDetailFetch(idOrderExpeditionPackage));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearOrderExpeditionPackageDetail());
        };
    }, [idOrderExpeditionPackage]);

    useEffect(() => {
        if (data) {
            setEditData({ ...data });
        }

        if (!idOrderExpeditionPackage && data?.ID) {
            navigate('/orderExpeditionPackages/' + String(data.ID));
        }
    }, [data]);

    useEffect(() => {
        if (deleted) {
            navigate(backUrl);
        }
    }, [deleted]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const formatData = { ...data };

            if (!formatData.WIDTH) formatData.WIDTH = null;
            if (!formatData.HEIGHT) formatData.HEIGHT = null;
            if (!formatData.LENGTH) formatData.LENGTH = null;
            if (!formatData.WEIGHT) formatData.WEIGHT = null;

            dispatch(
                updateOrderExpeditionPackageDetailFetch({
                    idOrderExpeditionPackage,
                    data: formatData
                })
            );
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    const handleDelete = () => {
        setConfirmParams({
            title: translate('orderExpeditionPackageDetail.deleteOrderExpeditionPackage'),
            content: translate('orderExpeditionPackageDetail.deleteOrderExpeditionPackageConfirm'),
            action: () => dispatch(deleteOrderExpeditionPackageDetailFetch(idOrderExpeditionPackage))
        });
    };

    return (
        <>
            <TopBlock
                data={data}
                idOrderExpeditionPackage={idOrderExpeditionPackage}
                disableEdit={disableEdit}
                permissionDelete={permissionDelete}
                handleSave={handleSave}
                handleDelete={handleDelete}
            />

            {loading && <Loading />}
            <RenderComponents
                formRef={formRef}
                data={data}
                onChange={onChange}
                editData={editData}
                editErrors={editErrors}
                disableEdit={disableEdit}
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default OrderExpeditionPackageDetailContainer;
